import { Link } from 'react-router-dom';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import { motion } from 'motion/react';
import { FileText, LineChart, Ship, BarChart3, Bot, ArrowRight, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { LandingNavbar } from '@/features/landing/components/landing-navbar';
import { ScrollReveal } from '@/components/motion/scroll-reveal';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface ModuleData {
  id: string;
  icon: LucideIcon;
  color: string;
  badgeBg: string;
  badgeText: string;
  accentBorder: string;
  title: string;
  description: string;
  bullets: string[];
}

const modules: ModuleData[] = [
  {
    id: 'cotizaciones',
    icon: FileText,
    color: 'blue',
    badgeBg: 'bg-blue-100 dark:bg-blue-900/40',
    badgeText: 'text-blue-600 dark:text-blue-400',
    accentBorder: 'border-blue-500/30',
    title: 'Cotizaciones profesionales en minutos',
    description:
      'Define modelos de pricing con formulas en cascada. Multiples secciones, materiales vinculados, descuentos e impuestos automaticos.',
    bullets: [
      'Modelos con formulas personalizables',
      'Multi-seccion con diferentes modelos',
      'PDF profesional en un clic',
      'Multi-moneda con tipos de cambio automaticos',
    ],
  },
  {
    id: 'pricing-dinamico',
    icon: LineChart,
    color: 'teal',
    badgeBg: 'bg-teal-100 dark:bg-teal-900/40',
    badgeText: 'text-teal-600 dark:text-teal-400',
    accentBorder: 'border-teal-500/30',
    title: 'Monitoreo de competencia en tiempo real',
    description:
      'Scraping automatico de precios en e-commerce. Alertas de cambios y reglas de ajuste automatico.',
    bullets: [
      'Scraping en Shopify, VTEX, Magento',
      'Alertas de cambio de precios',
      'Reglas automaticas de ajuste',
      'Historial y tendencias',
    ],
  },
  {
    id: 'importaciones',
    icon: Ship,
    color: 'indigo',
    badgeBg: 'bg-indigo-100 dark:bg-indigo-900/40',
    badgeText: 'text-indigo-600 dark:text-indigo-400',
    accentBorder: 'border-indigo-500/30',
    title: 'Costos de importación transparentes',
    description:
      'Registra cada importación con costos desglosados por incoterm. El sistema proratea y actualiza precios automáticamente.',
    bullets: [
      'Desglose por incoterm (FOB, CIF, DDP)',
      'Tipo de cambio automático',
      'Prorrateo por línea de producto',
      'Historial completo',
    ],
  },
  {
    id: 'analytics',
    icon: BarChart3,
    color: 'pink',
    badgeBg: 'bg-pink-100 dark:bg-pink-900/40',
    badgeText: 'text-pink-600 dark:text-pink-400',
    accentBorder: 'border-pink-500/30',
    title: 'Decisiones basadas en datos',
    description: 'Dashboards con win rate, márgenes, tendencias por vendedor y línea de negocio.',
    bullets: [
      'KPIs en tiempo real',
      'Reportes por vendedor y LOB',
      'Comparación de periodos',
      'Exportación a PDF y Excel',
    ],
  },
  {
    id: 'asistente-ia',
    icon: Bot,
    color: 'orange',
    badgeBg: 'bg-orange-100 dark:bg-orange-900/40',
    badgeText: 'text-orange-600 dark:text-orange-400',
    accentBorder: 'border-orange-500/30',
    title: 'Tu copiloto de pricing',
    description:
      'Pregunta lo que quieras en lenguaje natural. Analiza márgenes, sugiere estrategias, busca cotizaciones.',
    bullets: [
      'Análisis de márgenes y rentabilidad',
      'Sugerencias de pricing',
      'Búsqueda inteligente',
      'Detección de anomalias',
    ],
  },
];

const flowSteps = [
  { label: 'Importacion', icon: Ship },
  { label: 'Costo actualizado', icon: FileText },
  { label: 'Modelo de pricing', icon: LineChart },
  { label: 'Cotizacion', icon: FileText },
  { label: 'Aprobacion', icon: BarChart3 },
  { label: 'Analytics', icon: BarChart3 },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function PlatformPage() {
  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100 overflow-x-hidden bg-gradient-to-br from-white via-blue-50/40 to-violet-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Ambient blobs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-40 w-[600px] h-[600px] bg-blue-500/20 dark:bg-blue-500/10 rounded-full blur-[128px] animate-float" />
        <div
          className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-violet-500/20 dark:bg-violet-500/10 rounded-full blur-[128px] animate-float"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-cyan-500/15 dark:bg-cyan-500/5 rounded-full blur-[128px] animate-float"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <LandingNavbar />

      {/* Spacer for fixed navbar */}
      <div className="h-16" />

      {/* ---- Hero ---- */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <span className="inline-block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600 mb-4 tracking-wide uppercase">
              Plataforma
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
              Una plataforma,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">
                todo tu pricing
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Cotizaciones, pricing dinamico, importaciones, analytics e IA — cinco modulos que
              trabajan juntos para que vendas más y mejor.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---- Flow diagram ---- */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-0">
              {flowSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center gap-2 px-3 py-4 sm:px-5 rounded-xl bg-white/60 dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/60 backdrop-blur-sm min-w-[110px]">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-900/40 dark:to-violet-900/40 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 text-center leading-tight">
                        {step.label}
                      </span>
                    </div>
                    {i < flowSteps.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-600 mx-1 shrink-0 hidden sm:block" />
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Module sections ---- */}
      {modules.map((mod, i) => {
        const Icon = mod.icon;
        const isReversed = i % 2 !== 0;

        return (
          <section key={mod.id} id={mod.id} className="py-20 sm:py-28">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div
                className={`flex flex-col ${
                  isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'
                } items-center gap-12 lg:gap-20`}
              >
                {/* Text side */}
                <ScrollReveal
                  className="flex-1"
                  direction={isReversed ? 'right' : 'left'}
                  delay={0.1}
                >
                  <div>
                    {/* Badge */}
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${mod.badgeBg} mb-5`}
                    >
                      <Icon className={`w-4 h-4 ${mod.badgeText}`} />
                      <span
                        className={`text-xs font-semibold uppercase tracking-wider ${mod.badgeText}`}
                      >
                        {mod.id.replace('-', ' ')}
                      </span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                      {mod.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-8">
                      {mod.description}
                    </p>

                    <ul className="space-y-3">
                      {mod.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3">
                          <div
                            className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${mod.badgeText.replace(
                              'text-',
                              'bg-'
                            )}`}
                          />
                          <span className="text-gray-700 dark:text-gray-300">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>

                {/* Mockup / visual side */}
                <ScrollReveal
                  className="flex-1 w-full"
                  direction={isReversed ? 'left' : 'right'}
                  delay={0.2}
                >
                  <div
                    className={`relative rounded-2xl border ${mod.accentBorder} bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm p-8 sm:p-12 flex items-center justify-center min-h-[260px]`}
                  >
                    {/* Abstract decorative pattern */}
                    <div className="absolute inset-0 overflow-hidden rounded-2xl">
                      <div
                        className={`absolute -top-20 -right-20 w-60 h-60 ${mod.badgeBg} rounded-full blur-3xl opacity-40`}
                      />
                      <div
                        className={`absolute -bottom-16 -left-16 w-48 h-48 ${mod.badgeBg} rounded-full blur-3xl opacity-30`}
                      />
                    </div>
                    <div className="relative flex flex-col items-center gap-4">
                      <div
                        className={`w-16 h-16 rounded-2xl ${mod.badgeBg} flex items-center justify-center`}
                      >
                        <Icon className={`w-8 h-8 ${mod.badgeText}`} />
                      </div>
                      <div className="flex gap-2">
                        {[...Array(3)].map((_, j) => (
                          <div
                            key={j}
                            className={`h-2 rounded-full ${mod.badgeBg}`}
                            style={{ width: `${60 + j * 20}px` }}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2 mt-1">
                        {[...Array(4)].map((_, j) => (
                          <div
                            key={j}
                            className={`h-8 w-8 rounded-lg border ${mod.accentBorder} ${mod.badgeBg} opacity-60`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        );
      })}

      {/* ---- CTA ---- */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden">
              {/* Gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-violet-600 to-purple-700" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_60%)]" />

              <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Listo para transformar tu pricing?
                </h2>
                <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">
                  Empieza gratis y descubre como Prixia puede optimizar cada aspecto de tu
                  estrategia de precios.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <RegistroCta className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors shadow-lg shadow-blue-900/20">
                    Comenzar Gratis
                    <ArrowRight className="w-4 h-4" />
                  </RegistroCta>
                  <Link
                    to="/#pricing"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/30 text-white font-medium hover:bg-white/10 transition-colors"
                  >
                    Ver Pricing
                  </Link>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ---- Minimal footer ---- */}
      <footer className="border-t border-gray-200/60 dark:border-gray-800/60 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span>&copy; {new Date().getFullYear()} Prixia. Todos los derechos reservados.</span>
          <div className="flex gap-6">
            <Link
              to="/legal/privacy"
              className="hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Privacidad
            </Link>
            <Link
              to="/legal/terms"
              className="hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Terminos
            </Link>
            <Link
              to="/contact"
              className="hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Contacto
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
